import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';

// Components loaded eagerly (needed on every page)
import Navbar from './components/layout/Navbar';
import Sidebar from './components/layout/Sidebar';
import TabletsKioskBar from './components/layout/TabletsKioskBar';
import LoadingScreen from './components/common/LoadingScreen';
import ProtectedRoute from './components/auth/ProtectedRoute';

// Standalone Driver Tablets site: when served from the tablets.* hostname,
// the app renders only minimal chrome and lands on the dispatch board.
const IS_TABLETS_KIOSK = window.location.hostname.toLowerCase().startsWith('tablets.');

// Standalone driver upload portal: pod.lanita.us serves ONLY the DriverPortal,
// with no portal chrome and no other routes.
const IS_POD_HOST = window.location.hostname.toLowerCase().startsWith('pod.');

// Lazy-loaded pages - each page is only loaded when navigated to
// This dramatically reduces initial bundle size and speeds up first paint
const Login = lazy(() => import('./pages/Login'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const PageList = lazy(() => import('./pages/PageList'));
const PageViewer = lazy(() => import('./pages/PageViewer'));

// Incident Pages
const IncidentList = lazy(() => import('./pages/IncidentList'));
const IncidentViewer = lazy(() => import('./pages/IncidentViewer'));
const CreateIncident = lazy(() => import('./pages/CreateIncident'));
const IncidentDocumentViewer = lazy(() => import('./pages/IncidentDocumentViewer'));

// Password Pages
const PasswordList = lazy(() => import('./pages/PasswordList'));
const PasswordViewer = lazy(() => import('./pages/PasswordViewer'));
const CreatePassword = lazy(() => import('./pages/CreatePassword'));

// Note Pages
const NoteList = lazy(() => import('./pages/NoteList'));
const NoteViewer = lazy(() => import('./pages/NoteViewer'));
const CreateNote = lazy(() => import('./pages/CreateNote'));

// Employee Pages
const EmployeeList = lazy(() => import('./pages/EmployeeList'));
const EmployeeViewer = lazy(() => import('./pages/EmployeeViewer'));
const CreateEmployee = lazy(() => import('./pages/CreateEmployee'));

// Accounting Pages
const Accounting = lazy(() => import('./pages/Accounting'));
const QuickBooks = lazy(() => import('./pages/QuickBooks'));

// DocSign Pages
const DocSign = lazy(() => import('./pages/DocSign'));
const SignDocument = lazy(() => import('./pages/SignDocument'));

// Public application form + staff review pages
const ApplyPage = lazy(() => import('./pages/apply/ApplyPage'));

// Driver upload portal (public, email-code verified)
const DriverPortal = lazy(() => import('./pages/drivers/DriverPortal'));
// Public POD share viewer (ungated 30-day email link)
const SharedPodView = lazy(() => import('./pages/drivers/SharedPodView'));
const ApplicationsList = lazy(() => import('./pages/applications/ApplicationsList'));
const ApplicationViewer = lazy(() => import('./pages/applications/ApplicationViewer'));

// Water meter readings + usage stats
const WaterUsage = lazy(() => import('./pages/water/WaterUsage'));
const Fuel = lazy(() => import('./pages/fuel/Fuel'));

// Insurance policies, coverage, cost + policy documents
const Insurance = lazy(() => import('./pages/insurance/Insurance'));

// Live view of the Hikvision NVR cameras (+ the installable /cams app)
const Cameras = lazy(() => import('./pages/cameras/Cameras'));
const CamerasApp = lazy(() => import('./pages/cameras/CamerasApp'));
// Requests through the office HTTPS proxy, by site (canAccessTraffic)
const Traffic = lazy(() => import('./pages/traffic/Traffic'));
// Private + company file dropbox with public share links (canAccessDropbox)
const Dropbox = lazy(() => import('./pages/dropbox/Dropbox'));
// Driver docs the driver portal shows (canManagePortalDocs)
const PortalDocs = lazy(() => import('./pages/portalDocs/PortalDocs'));
// Public Dropbox share link page (/f/:token)
const SharedFile = lazy(() => import('./pages/dropbox/SharedFile'));

// Driver Analytics
const DriverAnalytics = lazy(() => import('./pages/DriverAnalytics'));

// Driver Tablets
const TabletsBoard = lazy(() => import('./pages/tablets/TabletsBoard'));
const TabletsDrivers = lazy(() => import('./pages/tablets/TabletsDrivers'));
const TabletsLoads = lazy(() => import('./pages/tablets/TabletsLoads'));
const TabletsTracking = lazy(() => import('./pages/tablets/TabletsTracking'));
const DriverPOD = lazy(() => import('./pages/tablets/DriverPOD'));
const TabletsRamps = lazy(() => import('./pages/tablets/TabletsRamps'));
// Read-only Driver docs (what drivers see on pod.lanita.us)
const TabletsDriverDocs = lazy(() => import('./pages/tablets/TabletsDriverDocs'));
const TabletsManage = lazy(() => import('./pages/tablets/TabletsManage'));
const TabletsPaySheets = lazy(() => import('./pages/tablets/TabletsPaySheets'));
const DriverPayroll = lazy(() => import('./pages/tablets/DriverPayroll'));
const TabletsDoors = lazy(() => import('./pages/tablets/TabletsDoors'));
const PcMiler = lazy(() => import('./pages/tablets/PcMiler'));
const MilerApp = lazy(() => import('./pages/tablets/MilerApp'));
const SharedDoorUnlock = lazy(() => import('./pages/tablets/SharedDoorUnlock'));
const RaffleChecker = lazy(() => import('./pages/RaffleChecker'));
const TabletDriverPage = lazy(() => import('./pages/tablets/TabletDriverPage'));
const TabletTripEdit = lazy(() => import('./pages/tablets/TabletTripEdit'));

// Admin Pages
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const UserManagement = lazy(() => import('./pages/admin/UserManagement'));
const PageManagement = lazy(() => import('./pages/admin/PageManagement'));
const CreateUser = lazy(() => import('./pages/admin/CreateUser'));
const EditUser = lazy(() => import('./pages/admin/EditUser'));
const CreatePage = lazy(() => import('./pages/admin/CreatePage'));
const EditPage = lazy(() => import('./pages/admin/EditPage'));
const ReportSubscriptions = lazy(() => import('./pages/admin/ReportSubscriptions'));
const InboundEmails = lazy(() => import('./pages/admin/InboundEmails'));

// User Pages
const Profile = lazy(() => import('./pages/Profile'));
const Plugins = lazy(() => import('./pages/Plugins'));
const ChangePassword = lazy(() => import('./pages/ChangePassword'));

// Error Pages
const NotFound = lazy(() => import('./pages/NotFound'));
const Forbidden = lazy(() => import('./pages/Forbidden'));

function App() {
  const { user, loading, isAuthenticated, canAccessDriverTablets, canAccessDoors } = useAuth();
  // The tablets site lands on the board; someone with only the Doors
  // permission can't open the board, so they land on Doors instead.
  const kioskHome = !canAccessDriverTablets() && canAccessDoors() ? '/driver-tablets/doors' : '/driver-tablets';

  // The pod.* host is the driver portal and nothing else — no portal auth
  // gate (drivers are not Users), no navbar/sidebar, every path renders it.
  if (IS_POD_HOST) {
    return (
      <ThemeProvider>
        <Suspense fallback={<LoadingScreen />}>
          <Routes>
            <Route path="/shared/:token" element={<SharedPodView />} />
            <Route path="*" element={<DriverPortal />} />
          </Routes>
        </Suspense>
      </ThemeProvider>
    );
  }

  // Public 50/50 raffle checker: no portal chrome and no auth wait.
  if (window.location.pathname === '/50' || window.location.pathname.startsWith('/50/')) {
    return (
      <ThemeProvider>
        <Suspense fallback={<LoadingScreen />}>
          <Routes>
            <Route path="/50" element={<RaffleChecker />} />
            <Route path="/50/*" element={<RaffleChecker />} />
          </Routes>
        </Suspense>
      </ThemeProvider>
    );
  }

  // Guest door-unlock links: no portal chrome and no auth wait. Opening the
  // URL must not pulse a door — the page only unlocks on an explicit tap.
  if (window.location.pathname.toLowerCase().startsWith('/d/')) {
    return (
      <ThemeProvider>
        <Suspense fallback={<LoadingScreen />}>
          <Routes>
            <Route path="/d/:token" element={<SharedDoorUnlock />} />
          </Routes>
        </Suspense>
      </ThemeProvider>
    );
  }

  // Dropbox share links: anyone with the link, no portal chrome or sign-in.
  // The page only shows the file; downloading takes an explicit tap.
  if (window.location.pathname.startsWith('/f/')) {
    return (
      <ThemeProvider>
        <Suspense fallback={<LoadingScreen />}>
          <Routes>
            <Route path="/f/:token" element={<SharedFile />} />
          </Routes>
        </Suspense>
      </ThemeProvider>
    );
  }

  // Standalone PC Miler app (installable from Edge/Chrome; index.html swaps in
  // its own manifest). Own app bar, no portal chrome; handles its own sign-in.
  const path = window.location.pathname;
  if (path === '/miler' || path.startsWith('/miler/')) {
    return (
      <ThemeProvider>
        <Suspense fallback={<LoadingScreen />}>
          <MilerApp />
        </Suspense>
      </ThemeProvider>
    );
  }

  // Standalone Cameras app (installable; index.html swaps in its manifest).
  // Portal hosts only — like /cameras, it stays off tablets.lanita.us.
  if (!IS_TABLETS_KIOSK && (path === '/cams' || path.startsWith('/cams/'))) {
    return (
      <ThemeProvider>
        <Suspense fallback={<LoadingScreen />}>
          <CamerasApp />
        </Suspense>
      </ThemeProvider>
    );
  }

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <ThemeProvider>
      <div className={`main-wrapper ${IS_TABLETS_KIOSK ? 'tablets-kiosk' : ''}`}>
        {user && (IS_TABLETS_KIOSK ? <TabletsKioskBar /> : <Navbar />)}
        
        <div className="d-flex app-body">
          {user && !IS_TABLETS_KIOSK && <Sidebar />}
          
          <div className="content-wrapper w-100">
            <Suspense fallback={<LoadingScreen />}>
            <Routes>
            {/* Public Routes */}
            <Route 
              path="/login" 
              element={!isAuthenticated ? <Login /> : <Navigate to={IS_TABLETS_KIOSK ? kioskHome : '/dashboard'} replace />} 
            />
            
            {/* Protected Routes */}
            <Route
              path="/"
              element={
                isAuthenticated
                  ? <Navigate to={IS_TABLETS_KIOSK ? kioskHome : '/dashboard'} replace />
                  : <Navigate to="/login" replace />
              }
            />
            
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/pages"
              element={
                <ProtectedRoute>
                  <PageList />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/pages/:pageId"
              element={
                <ProtectedRoute>
                  <PageViewer />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/incidents"
              element={
                <ProtectedRoute>
                  <IncidentList />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/incidents/create"
              element={
                <ProtectedRoute>
                  <CreateIncident />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/incidents/:incidentId"
              element={
                <ProtectedRoute>
                  <IncidentViewer />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/incidents/:incidentId/documents/:documentId"
              element={
                <ProtectedRoute>
                  <IncidentDocumentViewer />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/passwords"
              element={
                <ProtectedRoute>
                  <PasswordList />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/passwords/create"
              element={
                <ProtectedRoute>
                  <CreatePassword />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/passwords/:passwordId"
              element={
                <ProtectedRoute>
                  <PasswordViewer />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/notes"
              element={
                <ProtectedRoute>
                  <NoteList />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/notes/create"
              element={
                <ProtectedRoute>
                  <CreateNote />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/notes/:noteId"
              element={
                <ProtectedRoute>
                  <NoteViewer />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/employees"
              element={
                <ProtectedRoute>
                  <EmployeeList />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/employees/create"
              element={
                <ProtectedRoute>
                  <CreateEmployee />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/employees/:employeeId"
              element={
                <ProtectedRoute>
                  <EmployeeViewer />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/accounting"
              element={
                <ProtectedRoute requireAccounting>
                  <Accounting />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/quickbooks"
              element={
                <ProtectedRoute requireQuickBooks>
                  <QuickBooks />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/docsign"
              element={
                <ProtectedRoute requireDocSign>
                  <DocSign />
                </ProtectedRoute>
              }
            />

            <Route
              path="/driver-analytics"
              element={
                <ProtectedRoute requireDriverAnalytics>
                  <DriverAnalytics />
                </ProtectedRoute>
              }
            />

            <Route
              path="/driver-tablets"
              element={
                <ProtectedRoute requireDriverTablets>
                  <TabletsBoard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/driver-tablets/drivers"
              element={
                <ProtectedRoute requireDriverTablets>
                  <TabletsDrivers />
                </ProtectedRoute>
              }
            />

            <Route
              path="/driver-tablets/loads"
              element={
                <ProtectedRoute requireDriverTablets>
                  <TabletsLoads />
                </ProtectedRoute>
              }
            />

            <Route
              path="/driver-tablets/map"
              element={
                <ProtectedRoute requireDriverTablets requireTabletTab="liveMap">
                  <TabletsTracking />
                </ProtectedRoute>
              }
            />

            <Route
              path="/driver-tablets/pod"
              element={
                <ProtectedRoute requireDriverTablets requireTabletTab="driverPod">
                  <DriverPOD />
                </ProtectedRoute>
              }
            />

            <Route
              path="/driver-tablets/ramps"
              element={
                <ProtectedRoute requireDriverTablets requireTabletTab="equipment">
                  <TabletsRamps />
                </ProtectedRoute>
              }
            />

            {/* Read-only for everyone with Driver Tablets; editing is the portal's /driver-docs */}
            <Route
              path="/driver-tablets/docs"
              element={
                <ProtectedRoute requireDriverTablets>
                  <TabletsDriverDocs />
                </ProtectedRoute>
              }
            />

            {/* Old POD-upload email links pointed here; send them to the POD page */}
            <Route
              path="/driver-tablets/uploads"
              element={<Navigate to="/driver-tablets/pod" replace />}
            />

            <Route
              path="/driver-tablets/pay"
              element={
                <ProtectedRoute requireDriverTablets requireTabletTab="paySheets">
                  <TabletsPaySheets />
                </ProtectedRoute>
              }
            />

            <Route
              path="/driver-tablets/payroll"
              element={
                <ProtectedRoute requireDriverTablets requireTabletTab="driverPayroll">
                  <DriverPayroll />
                </ProtectedRoute>
              }
            />

            <Route
              path="/driver-tablets/payroll/:batchId"
              element={
                <ProtectedRoute requireDriverTablets requireTabletTab="driverPayroll">
                  <DriverPayroll />
                </ProtectedRoute>
              }
            />

            {/* Doors has its own permission (canAccessDoors), separate from
                Driver Tablets, but still lives at this path on the tablets site */}
            <Route
              path="/driver-tablets/doors"
              element={
                <ProtectedRoute requireDoors>
                  <TabletsDoors />
                </ProtectedRoute>
              }
            />
            {/* Same Doors page, linked from the portal nav (its own path so the
                sidebar doesn't light up Driver Tablets too) */}
            <Route
              path="/doors"
              element={
                <ProtectedRoute requireDoors>
                  <TabletsDoors />
                </ProtectedRoute>
              }
            />

            <Route
              path="/driver-tablets/pcmiler"
              element={
                <ProtectedRoute requireDriverTablets>
                  <PcMiler />
                </ProtectedRoute>
              }
            />

            <Route
              path="/driver-tablets/manage"
              element={
                <ProtectedRoute requireDriverTablets>
                  <TabletsManage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/driver-tablets/driver/:driverId"
              element={
                <ProtectedRoute requireDriverTablets>
                  <TabletDriverPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/driver-tablets/driver/:driverId/trip"
              element={
                <ProtectedRoute requireDriverTablets>
                  <TabletTripEdit />
                </ProtectedRoute>
              }
            />
            
            {/* Public signing route - NO AUTH REQUIRED */}
            <Route
              path="/sign/:token"
              element={<SignDocument />}
            />

            {/* Public application form - NO AUTH REQUIRED (email-code verified) */}
            <Route path="/app" element={<ApplyPage />} />
            <Route path="/app/:slug" element={<ApplyPage />} />

            {/* Driver upload portal - NO AUTH REQUIRED (email-code verified, employees only) */}
            <Route path="/drivers" element={<DriverPortal />} />

            {/* Public POD share viewer - NO AUTH REQUIRED (unguessable token) */}
            <Route path="/shared/:token" element={<SharedPodView />} />

            {/* Public 50/50 raffle checker — also served via the /50 early return */}
            <Route path="/50" element={<RaffleChecker />} />

            {/* Staff application review */}
            <Route
              path="/applications"
              element={
                <ProtectedRoute requireApplications>
                  <ApplicationsList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/applications/:id"
              element={
                <ProtectedRoute requireApplications>
                  <ApplicationViewer />
                </ProtectedRoute>
              }
            />

            {/* Portal only — not part of the tablets site */}
            <Route
              path="/fuel"
              element={IS_TABLETS_KIOSK ? <Navigate to="/driver-tablets" replace /> : (
                <ProtectedRoute requireFuel>
                  <Fuel />
                </ProtectedRoute>
              )}
            />
            <Route
              path="/water-usage"
              element={IS_TABLETS_KIOSK ? <Navigate to="/driver-tablets" replace /> : (
                <ProtectedRoute requireWaterUsage>
                  <WaterUsage />
                </ProtectedRoute>
              )}
            />
            <Route
              path="/insurance"
              element={IS_TABLETS_KIOSK ? <Navigate to="/driver-tablets" replace /> : (
                <ProtectedRoute requireInsurance>
                  <Insurance />
                </ProtectedRoute>
              )}
            />
            <Route
              path="/cameras"
              element={IS_TABLETS_KIOSK ? <Navigate to="/driver-tablets" replace /> : (
                <ProtectedRoute requireCameras>
                  <Cameras />
                </ProtectedRoute>
              )}
            />
            <Route
              path="/traffic"
              element={IS_TABLETS_KIOSK ? <Navigate to="/driver-tablets" replace /> : (
                <ProtectedRoute requireTraffic>
                  <Traffic />
                </ProtectedRoute>
              )}
            />
            <Route
              path="/dropbox"
              element={IS_TABLETS_KIOSK ? <Navigate to="/driver-tablets" replace /> : (
                <ProtectedRoute requireDropbox>
                  <Dropbox />
                </ProtectedRoute>
              )}
            />
            <Route
              path="/driver-docs"
              element={IS_TABLETS_KIOSK ? <Navigate to="/driver-tablets" replace /> : (
                <ProtectedRoute requirePortalDocs>
                  <PortalDocs />
                </ProtectedRoute>
              )}
            />
            
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />

            <Route
              path="/plugins"
              element={
                <ProtectedRoute>
                  <Plugins />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/change-password"
              element={
                <ProtectedRoute>
                  <ChangePassword />
                </ProtectedRoute>
              }
            />
            
            {/* Admin Routes */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute requireAdmin>
                  <Navigate to="/admin/dashboard" replace />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute requireAdmin>
                  <UserManagement />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/admin/users/create"
              element={
                <ProtectedRoute requireAdmin>
                  <CreateUser />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/admin/users/:userId/edit"
              element={
                <ProtectedRoute requireAdmin>
                  <EditUser />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/admin/pages"
              element={
                <ProtectedRoute requirePageManager>
                  <PageManagement />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/admin/pages/create"
              element={
                <ProtectedRoute requirePageManager>
                  <CreatePage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/admin/pages/:pageId/edit"
              element={
                <ProtectedRoute requirePageManager>
                  <EditPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/reports"
              element={
                <ProtectedRoute requireAdmin>
                  <ReportSubscriptions />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/inbound-emails"
              element={
                <ProtectedRoute requireAdmin>
                  <InboundEmails />
                </ProtectedRoute>
              }
            />

            {/* Error Routes */}
            <Route path="/forbidden" element={<Forbidden />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
            </Suspense>
        </div>
      </div>
      </div>
    </ThemeProvider>
  );
}

export default App; 